import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, Loader2, Mail, Lock, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';

// Logo SVG Component
const Logo = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 200 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 25 L25 10 L40 25 L25 18 L10 25Z" fill="#1B4F72" />
    <path d="M25 18 L40 25 L25 40 L25 32 L15 25 L25 18Z" fill="#D4A03A" fillOpacity="0.9" />
    <text x="50" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="700" fill="#1B4F72">GRC</text>
    <text x="95" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="400" fill="#64748B">educators</text>
    <text x="50" y="45" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="8" fontWeight="500" fill="#D4A03A" letterSpacing="0.1em">YOUR PATH TO EXCELLENCE</text>
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(redirectTo);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-start justify-center p-8 bg-white">
        <div className="w-full max-w-md pt-24">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-zinc-950 mb-2">
              Welcome back
            </h1>
            <p className="text-zinc-500">
              Sign in to continue your learning journey
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert className="bg-red-50 border-red-200 rounded-xl">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-700 font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-14 rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 text-base"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-zinc-700 font-medium">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-zinc-900 hover:text-emerald-700 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 h-14 rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 text-base"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-14 bg-zinc-900 hover:bg-zinc-800 text-white font-semibold text-base rounded-md transition-colors"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <span className="text-zinc-500">Don't have an account? </span>
            <Link
              to="/register"
              className="text-zinc-900 hover:text-emerald-700 font-semibold"
            >
              Create account
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Image/Branding */}
      <div className="hidden lg:flex flex-1 bg-[#09090b] relative overflow-hidden">
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center p-16 text-center">
          <div className="mb-8">
            <Sparkles className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="font-heading text-4xl font-bold text-white mb-4">
              Advance Your Career
            </h2>
            <p className="text-zinc-400 text-lg max-w-md">
              Join thousands of professionals who trust GRC Educators for their compliance training.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-12">
            {[
              { value: '15K+', label: 'Professionals' },
              { value: '100+', label: 'Webinars' },
              { value: '50+', label: 'Experts' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl font-bold text-emerald-400 font-heading">{stat.value}</div>
                <div className="text-zinc-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div className="mt-16 bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md">
            <p className="text-white/90 italic mb-4">
              "GRC Educators has been invaluable for keeping our team compliant. The quality of content is exceptional."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/30 flex items-center justify-center">
                <span className="text-emerald-300 font-semibold">JD</span>
              </div>
              <div className="text-left">
                <p className="text-white font-medium text-sm">John Davidson</p>
                <p className="text-zinc-400 text-xs">Chief Compliance Officer</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
